import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clientsApi, api, commissionsApi, resellerMarkupApi, ResellerMarkupChannel, Client, Manager } from '../services/api';
import { RequisitesFields } from './RequisitesFields';
import { getBranding } from '../config/branding';
import '../styles/reports.css';
import '../styles/users.css';
import { useTranslation, Trans } from 'react-i18next';
import i18n, { currentLocale } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

// External platforms shown in the Clients "Max" column. `key` matches the
// backend's external_platforms values; short letter + colour render as a chip.
const MAX_PLATFORM_META: { key: string; label: string; name: string; color: string }[] = [
  { key: 'google_ads', label: 'G', name: 'Google Ads', color: '#4285F4' },
  { key: 'meta', label: 'M', name: 'Meta', color: '#0866FF' },
  { key: 'tiktok', label: 'T', name: 'TikTok', color: '#111111' },
  { key: 'yandex', label: 'Y', name: 'Yandex', color: '#FC3F1D' },
  { key: 'linkedin', label: 'L', name: 'LinkedIn', color: '#0A66C2' },
];

interface Office {
  id: number;
  code: string;
  name: string;
  is_hub?: boolean;
  currency?: string;
}

interface CreateClientForm {
  name: string;
  domain: string;
  regional_office_id: number | null;
}

const validateDomain = (domain: string): string | null => {
  if (!domain) return null; // optional field
  if (!domain.startsWith('http://') && !domain.startsWith('https://')) {
    return i18n.t('clients.errDomainScheme');
  }
  // Basic URL validation
  try {
    new URL(domain);
    return null;
  } catch {
    return i18n.t('clients.errDomainUrl');
  }
};

const ClientsPage: React.FC = () => {
  // Yandex Direct is a Sense-only integration: a white-label instance has no
  // Yandex application to authorise against, so it is neither shown nor offered
  // there. Read per render, NOT at module scope — branding is fetched from the
  // backend and index.tsx renders only after it resolves, but module bodies run
  // at import time, before that, so a module-level read always sees the Sense
  // defaults and would show the block on every edition.
  const senseEdition = getBranding().edition === 'sense';
  const yandexOffered = senseEdition;
  // LinkedIn is hidden on a white-label for now — the cabinet column, the adapter
  // and any markup rate already configured stay untouched, there is just nothing
  // to bind a new cabinet with and no markup line to fill in.
  const linkedinOffered = senseEdition;
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [commissionMap, setCommissionMap] = useState<Record<number, { commission_percent: number; source: string; model_type: string }>>({});
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tabs and Filters
  const [activeTab, setActiveTab] = useState<'active' | 'paused' | 'archived'>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [officeFilter, setOfficeFilter] = useState<string>('');
  const [showUnassigned, setShowUnassigned] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newClientCredentials, setNewClientCredentials] = useState<{ email: string; password: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Archive/Restore
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  // Saved fine, but the platform still needs something done — shown on its own so it
  // isn't lost at the top of a long form.
  const [savedWarnings, setSavedWarnings] = useState<string[] | null>(null);
  const [archiveClient, setArchiveClient] = useState<Client | null>(null);
  const [archiveAction, setArchiveAction] = useState<'archive' | 'restore'>('archive');
  const [archiving, setArchiving] = useState(false);

  // Permanent delete (admin only, archived clients)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteClient, setDeleteClient] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [editForm, setEditForm] = useState<{ display_name: string; regional_office_id: number | null; manager_ids: number[]; agency_id: number | null; discount_percent: number; client_markup: number | null; billing_model: string; google_ads_customer_id: string; meta_ad_account_id: string; tiktok_advertiser_id: string; yandex_client_login: string; linkedin_ad_account_id: string; meta_page_id: string; meta_page_name: string; tiktok_identity_id: string; tiktok_identity_type: string; tiktok_identity_name: string; tiktok_identity_bc_id: string; linkedin_org_id: string; linkedin_org_name: string; meta_dsa_beneficiary: string; meta_dsa_payor: string; legal_name: string; tax_id: string; legal_address: string; contract_number: string }>({ display_name: '', regional_office_id: null, manager_ids: [], agency_id: null, discount_percent: 0, client_markup: null, billing_model: 'prepaid', google_ads_customer_id: '', meta_ad_account_id: '', tiktok_advertiser_id: '', yandex_client_login: '', linkedin_ad_account_id: '', meta_page_id: '', meta_page_name: '', tiktok_identity_id: '', tiktok_identity_type: '', tiktok_identity_name: '', tiktok_identity_bc_id: '', linkedin_org_id: '', linkedin_org_name: '', meta_dsa_beneficiary: '', meta_dsa_payor: '', legal_name: '', tax_id: '', legal_address: '', contract_number: '' });
  const [externalSynced, setExternalSynced] = useState<string | null>(null);
  // Per-client account-ownership override (own vs client accounts) for the edit modal.
  const [ownership, setOwnership] = useState<{ model_type: string; is_override: boolean; inherited: { entity_type: string; model_type: string } } | null>(null);
  const [ownershipBusy, setOwnershipBusy] = useState(false);
  // Per-platform reseller markup (baked into external spend on OWN cabinets). Keyed by channel.
  const [resellerMarkup, setResellerMarkup] = useState<Record<string, ResellerMarkupChannel> | null>(null);
  const [resellerBusy, setResellerBusy] = useState<string | null>(null);
  // Draft inputs per channel for adding a new markup stage.
  const [resellerDraft, setResellerDraft] = useState<Record<string, { pct: string; from: string }>>({});
  // Per-client commission: an advertiser-level manual stage is the HIGHEST-priority rate
  // (overrides the agency); with none set the client inherits the agency's rate.
  const [clientComm, setClientComm] = useState<{ current_commission: any; stages: any[] } | null>(null);
  const [clientCommBusy, setClientCommBusy] = useState(false);
  const [clientCommDraft, setClientCommDraft] = useState<{ pct: string; from: string }>({ pct: '', from: '' });
  // Per-client Sense minimum budget share (0–35%). Floor the Sense slice can be dragged to in a
  // manual Sense Max budget split; inherits client → agency/freelancer → default.
  const [senseMin, setSenseMin] = useState<{ own: number | null; current: { min_percent: number; source: string; level: string | null }; default_percent: number } | null>(null);
  const [senseMinDraft, setSenseMinDraft] = useState('');
  const [senseMinBusy, setSenseMinBusy] = useState(false);
  // External account picker (Google Ads / Meta) — populated after Connect
  const [gAccounts, setGAccounts] = useState<{ id: string; name: string | null; login_customer_id?: string | null; type?: string; rawName?: string | null; bc_id?: string | null; owner?: string; owner_id?: string; linked?: boolean; linked_to?: string | null; first_seen?: string | null }[]>([]);
  const [gLoading, setGLoading] = useState(false);
  const [pickerKind, setPickerKind] = useState<'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin' | 'meta_page' | 'tiktok_identity' | 'linkedin_org' | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerShowAll, setPickerShowAll] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [retryingCreation, setRetryingCreation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [managers, setManagers] = useState<Manager[]>([]);

  // Create form
  const [createForm, setCreateForm] = useState<CreateClientForm>({
    name: '',
    domain: '',
    regional_office_id: null,
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [domainTouched, setDomainTouched] = useState(false);

  // Agency filter
  const [agencyFilter, setAgencyFilter] = useState<string>('');
  const [agencies, setAgencies] = useState<{ id: number; name: string; regional_office_id: number | null }[]>([]);

  // Default client markup (for agency_admin and freelancer)
  // Click reconciliation — admin only. A list of periods: each factor runs from
  // its date until the next one starts. Saved immediately, not on the dialog's
  // Save, so adding a period can't ride along with an unrelated edit.
  type ClickAdj = { effective_from: string; factor: number; reason: string | null; updated_by_email: string | null };
  const [clickAdjs, setClickAdjs] = useState<ClickAdj[]>([]);
  const [clickFactorInput, setClickFactorInput] = useState<string>('');
  const [clickFromInput, setClickFromInput] = useState<string>('');
  const [clickReasonInput, setClickReasonInput] = useState<string>('');
  const [clickAdjBusy, setClickAdjBusy] = useState(false);
  const [clickAdjError, setClickAdjError] = useState('');
  const [defaultClientMarkup, setDefaultClientMarkup] = useState<number>(0);
  const [defaultClientMarkupInput, setDefaultClientMarkupInput] = useState<string>('0');
  const [savingDefaultMarkup, setSavingDefaultMarkup] = useState(false);
  const [showMarkupEditor, setShowMarkupEditor] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const isRegionalAdmin = currentUser?.role === 'regional_admin';
  const isManager = currentUser?.role === 'manager';
  const isAgencyAdmin = currentUser?.role === 'agency_admin';
  const isAgencyManager = currentUser?.role === 'agency_manager';
  const isFreelancer = currentUser?.role === 'freelancer';

  // Direct client = not under agency, not freelancer-owned, not self-service
  const isDirectClient = (c: Client) => !c.agency_id && !c.has_freelancer && !c.is_self_service;

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  // Fetch offices
  const fetchOffices = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/regional-offices`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setOffices(data.offices || []);
      }
    } catch (err) {
      console.error('Failed to fetch offices:', err);
    }
  }, [getAuthHeaders]);

  // Fetch agencies
  const fetchAgencies = useCallback(async () => {
    if (isAgencyAdmin || isAgencyManager) return; // Agency roles don't need agency filter
    try {
      const response = await fetch(`${API_BASE}/api/admin/agencies?status=active`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAgencies(data.agencies || []);
      }
    } catch {}
  }, [getAuthHeaders, isAgencyAdmin]);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { status: activeTab };
      if (searchQuery) params.search = searchQuery;
      if (officeFilter) params.regional_office_id = parseInt(officeFilter);
      if (agencyFilter) params.agency_id = parseInt(agencyFilter);
      if (showUnassigned) params.unassigned = true;

      const data = await clientsApi.list(params);
      setClients(data.clients);
    } catch (err) {
      setError(t('clients.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery, officeFilter, agencyFilter, showUnassigned, t]);

  // Fetch default client markup for agency_admin/freelancer
  const fetchDefaultMarkup = useCallback(async () => {
    if (!isAgencyAdmin && !isFreelancer) return;
    try {
      const data = await clientsApi.getMyMarkup();
      setDefaultClientMarkup(data.default_client_markup);
      setDefaultClientMarkupInput(String(data.default_client_markup));
    } catch {}
  }, [isAgencyAdmin, isFreelancer]);

  const handleSaveDefaultMarkup = async () => {
    const val = parseFloat(defaultClientMarkupInput) || 0;
    if (val < 0) return;
    setSavingDefaultMarkup(true);
    try {
      await clientsApi.updateMyMarkup(val);
      setDefaultClientMarkup(val);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('clients.errSaveMarkup'));
    } finally {
      setSavingDefaultMarkup(false);
    }
  };

  useEffect(() => {
    fetchOffices();
    fetchAgencies();
    fetchDefaultMarkup();
  }, [fetchOffices, fetchAgencies, fetchDefaultMarkup]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // The commission entity for a client: self-service → itself, else its agency /
  // freelancer. Resolve effective commission + account model for the list columns.
  const loadCommissions = useCallback(async (list: Client[]) => {
    const groups: Record<string, { clientId: number; eid: string }[]> = {};
    list.forEach(c => {
      // Commission applies to any client with at least one external platform connected.
      const hasExternal = (c.external_platforms?.length || 0) > 0;
      const e = !hasExternal ? null
        : c.agency_id ? { type: 'agency', id: String(c.agency_id) }
        : (c.has_freelancer && c.freelancer_user) ? { type: 'freelancer', id: String(c.freelancer_user.id) }
        : { type: 'self_service', id: c.advertiser_id };  // self-service OR direct → the client itself
      if (e) (groups[e.type] ||= []).push({ clientId: c.id, eid: e.id });
    });
    const map: Record<number, any> = {};
    await Promise.all(Object.entries(groups).map(async ([type, arr]) => {
      try {
        const { results } = await commissionsApi.bulk(type, arr.map(a => a.eid));
        arr.forEach(a => { if (results[a.eid]) map[a.clientId] = results[a.eid]; });
      } catch { /* non-blocking */ }
    }));
    setCommissionMap(map);
  }, []);
  useEffect(() => { loadCommissions(clients); }, [clients, loadCommissions]);

  // After the Google Ads OAuth callback we land on /clients?google_connected=1&adv=…
  // — auto-open that client and (if no single account was auto-resolved) load the
  // account picker.
  const gRedirectHandled = useRef(false);
  useEffect(() => {
    if (gRedirectHandled.current || clients.length === 0) return;
    const sp = new URLSearchParams(window.location.search);
    const kind: 'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin' | null =
      sp.get('google_connected') === '1' ? 'google'
        : sp.get('meta_connected') === '1' ? 'meta'
          : sp.get('tiktok_connected') === '1' ? 'tiktok'
            : sp.get('yandex_connected') === '1' ? 'yandex'
              : sp.get('linkedin_connected') === '1' ? 'linkedin' : null;
    if (!kind) return;
    const adv = sp.get('adv');
    const client = adv ? clients.find((c) => c.advertiser_id === adv) : null;
    gRedirectHandled.current = true;
    window.history.replaceState({}, '', '/clients');
    if (client) {
      openEditModal(client).then(() => {
        if (sp.get('pick') === '1' || !sp.get('customer_id')) loadExternalAccounts(kind, adv!);
      });
    }
  }, [clients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc key to close modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (savedWarnings) setSavedWarnings(null);
        if (showArchiveModal) { setShowArchiveModal(false); setArchiveClient(null); }
        if (showCreateModal) setShowCreateModal(false);
        if (showEditModal) { setShowEditModal(false); setEditClient(null); setEditError(''); }
        if (showCredentialsModal) {
          setShowCredentialsModal(false);
          setNewPassword('');
          setShowPassword(false);
          setSelectedClient(null);
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCreateModal, showCredentialsModal, showArchiveModal, showEditModal, savedWarnings]);

  // Create client
  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');

    if (!createForm.name.trim()) {
      setCreateError(t('clients.errNameRequired'));
      return;
    }

    // Validate domain if provided
    const domainError = validateDomain(createForm.domain.trim());
    if (domainError) {
      setCreateError(domainError);
      return;
    }

    setCreating(true);
    try {
      const result = await clientsApi.create({
        name: createForm.name.trim(),
        domain: createForm.domain.trim() || undefined,
        regional_office_id: createForm.regional_office_id || undefined,
      });

      setNewClientCredentials({
        email: result.user_email,
        password: result.password,
      });

      setShowCreateModal(false);
      setCreateForm({
        name: '',
        domain: '',
        regional_office_id: null,
      });
      setDomainTouched(false);
      setCreateError('');

      await fetchClients();
    } catch (err: any) {
      setCreateError(err.response?.data?.detail || t('clients.errCreate'));
    } finally {
      setCreating(false);
    }
  };

  // Reset password (called from credentials modal)
  const handleResetPassword = async () => {
    if (!selectedClient?.advertiser_id) return;

    setResettingPassword(true);
    try {
      const result = await clientsApi.resetPassword(selectedClient.advertiser_id);
      setNewPassword(result.password);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('clients.errResetPassword'));
    } finally {
      setResettingPassword(false);
    }
  };

  // Update office assignment
  const handleUpdateOffice = async (client: Client, officeId: number | null) => {
    if (!client.advertiser_id) return;

    try {
      await clientsApi.updateOffice(client.advertiser_id, officeId);
      await fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('clients.errUpdateOffice'));
    }
  };

  // Archive/Restore client
  const handleArchiveRestore = async () => {
    if (!archiveClient?.advertiser_id) return;
    setArchiving(true);
    try {
      if (archiveAction === 'archive') {
        await clientsApi.archive(archiveClient.advertiser_id);
      } else {
        await clientsApi.restore(archiveClient.advertiser_id);
      }
      setShowArchiveModal(false);
      setArchiveClient(null);
      await fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.detail
        || t(archiveAction === 'archive' ? 'clients.errArchive' : 'clients.errRestore'));
    } finally {
      setArchiving(false);
    }
  };

  // Permanently delete an archived client (admin only)
  const handleDelete = async () => {
    if (!deleteClient?.advertiser_id) return;
    setDeleting(true);
    try {
      await clientsApi.remove(deleteClient.advertiser_id);
      setShowDeleteModal(false);
      setDeleteClient(null);
      await fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('clients.errDelete'));
    } finally {
      setDeleting(false);
    }
  };

  // Open edit modal
  const openEditModal = async (client: Client) => {
    setEditClient(client);
    setEditForm({
      display_name: client.display_name,
      regional_office_id: client.regional_office_id,
      manager_ids: (client.managers || []).map(m => m.id),
      agency_id: client.agency_id ?? null,
      discount_percent: client.discount_percent || 0,
      client_markup: client.client_markup,
      billing_model: client.billing_model || 'prepaid',
      google_ads_customer_id: '',
      meta_ad_account_id: '',
      tiktok_advertiser_id: '',
      yandex_client_login: '',
      linkedin_ad_account_id: '',
      meta_page_id: '',
      meta_page_name: '',
      tiktok_identity_id: '',
      tiktok_identity_type: '',
      tiktok_identity_name: '',
      tiktok_identity_bc_id: '',
      linkedin_org_id: '',
      linkedin_org_name: '',
      meta_dsa_beneficiary: '',
      meta_dsa_payor: '',
      legal_name: '',
      tax_id: '',
      legal_address: '',
      contract_number: '',
    });
    setEditError('');
    setSyncResult(null);
    setExternalSynced(null);
    setGAccounts([]);
    setPickerKind(null);
    setClickAdjs([]);
    setClickFactorInput('');
    setClickFromInput('');
    setClickReasonInput('');
    setClickAdjError('');
    setShowEditModal(true);

    // Fetch full client details (includes external platform IDs)
    if (client.advertiser_id) {
      try {
        const detail = await clientsApi.get(client.advertiser_id);
        setEditForm((prev) => ({
          ...prev,
          google_ads_customer_id: (detail as any).google_ads_customer_id || '',
          meta_ad_account_id: (detail as any).meta_ad_account_id || '',
          tiktok_advertiser_id: (detail as any).tiktok_advertiser_id || '',
          yandex_client_login: (detail as any).yandex_client_login || '',
          linkedin_ad_account_id: (detail as any).linkedin_ad_account_id || '',
          meta_page_id: (detail as any).meta_page_id || '',
          meta_page_name: (detail as any).meta_page_name || '',
          tiktok_identity_id: (detail as any).tiktok_identity_id || '',
          tiktok_identity_type: (detail as any).tiktok_identity_type || '',
          tiktok_identity_name: (detail as any).tiktok_identity_name || '',
          tiktok_identity_bc_id: (detail as any).tiktok_identity_bc_id || '',
          linkedin_org_id: (detail as any).linkedin_org_id || '',
          linkedin_org_name: (detail as any).linkedin_org_name || '',
          meta_dsa_beneficiary: (detail as any).meta_dsa_beneficiary || '',
          meta_dsa_payor: (detail as any).meta_dsa_payor || '',
          legal_name: (detail as any).legal_name || '',
          tax_id: (detail as any).tax_id || '',
          legal_address: (detail as any).legal_address || '',
          contract_number: (detail as any).contract_number || '',
        }));
        setExternalSynced((detail as any).external_synced_at || null);
        // Only present for admins — the backend omits it for everyone else.
        setClickAdjs((detail as any).click_adjustments || []);
      } catch {
        // fall through with empty values
      }
    }

    // Account-ownership override (admins/regional admins only)
    if ((isAdmin || isRegionalAdmin) && client.advertiser_id) {
      try { setOwnership(await commissionsApi.getClientOwnership(client.advertiser_id)); }
      catch { setOwnership(null); }
      // Per-platform reseller markup (only meaningful for own_accounts, shown below).
      try { setResellerMarkup((await resellerMarkupApi.get(client.advertiser_id)).channels); }
      catch { setResellerMarkup(null); }
      // Per-client commission (advertiser stage = highest priority, else inherits agency).
      try { setClientComm(await commissionsApi.getEntityCommission('advertiser', client.advertiser_id)); }
      catch { setClientComm(null); }
      // Per-client Sense minimum budget share.
      try { const sm = await commissionsApi.getSenseMin('advertiser', client.advertiser_id); setSenseMin(sm); setSenseMinDraft(sm.own != null ? String(sm.own) : ''); }
      catch { setSenseMin(null); setSenseMinDraft(''); }
    } else {
      setOwnership(null);
      setResellerMarkup(null);
      setClientComm(null);
      setSenseMin(null); setSenseMinDraft('');
    }
    setResellerDraft({});

    // Fetch managers filtered by client's office (0 = Central Hub)
    try {
      const data = await clientsApi.getManagers(client.regional_office_id ?? 0, client.agency_id);
      setManagers(data.managers);
    } catch {
      setManagers([]);
    }
  };

  // Connect Google Ads via OAuth — redirects to Google's consent screen; the
  // backend callback stores the per-client refresh token + customer id, then
  // bounces back to /clients?google_connected=1.
  // Kick off the OAuth Connect for an external platform (redirects to the provider).
  const connectExternal = async (kind: 'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin') => {
    if (!editClient?.advertiser_id) return;
    const path = kind === 'google' ? 'google-ads' : kind;
    try {
      const resp = await api.get(`/api/oauth/${path}/start?advertiser_id=${editClient.advertiser_id}`);
      if (resp.data?.auth_url) window.location.href = resp.data.auth_url;
    } catch (err: any) {
      setSyncResult(`${t('clients.errConnect')} ${err.response?.data?.detail || err.message}`);
    }
  };

  // Fully disconnect a platform — clears the cabinet id AND the stored OAuth token.
  const disconnectExternal = async (kind: 'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin') => {
    if (!editClient?.advertiser_id) return;
    const formKey = ({ google: 'google_ads_customer_id', meta: 'meta_ad_account_id', tiktok: 'tiktok_advertiser_id',
      yandex: 'yandex_client_login', linkedin: 'linkedin_ad_account_id' } as const)[kind];
    try {
      await clientsApi.disconnectExternal(editClient.advertiser_id, kind);
      // Clearing the ad account also clears the sender identity it belonged to.
      const cleared: any = { [formKey]: '' };
      if (kind === 'meta') { cleared.meta_page_id = ''; cleared.meta_page_name = ''; }
      setEditForm({ ...editForm, ...cleared });
      await fetchClients();
    } catch (err: any) {
      setSyncResult(`${t('clients.errDisconnect')} ${err.response?.data?.detail || err.message}`);
    }
  };

  // Account ownership: pin this client to own/client accounts, or reset to inherited.
  const applyOwnership = async (model: string) => {
    if (!editClient?.advertiser_id || ownershipBusy) return;
    setOwnershipBusy(true);
    try {
      await commissionsApi.setClientOwnership(editClient.advertiser_id, model);
      setOwnership(await commissionsApi.getClientOwnership(editClient.advertiser_id));
      loadCommissions(clients);
    } catch (err: any) {
      setSyncResult(`${t('clients.errOwnership')} ${err.response?.data?.detail || err.message}`);
    } finally { setOwnershipBusy(false); }
  };
  const resetOwnership = async () => {
    if (!editClient?.advertiser_id || ownershipBusy) return;
    setOwnershipBusy(true);
    try {
      await commissionsApi.clearClientOwnership(editClient.advertiser_id);
      setOwnership(await commissionsApi.getClientOwnership(editClient.advertiser_id));
      loadCommissions(clients);
    } catch (err: any) {
      setSyncResult(`${t('clients.errOwnership')} ${err.response?.data?.detail || err.message}`);
    } finally { setOwnershipBusy(false); }
  };

  // Reseller markup: add a new effective-dated stage for a channel, or delete one.
  const reloadReseller = async () => {
    if (!editClient?.advertiser_id) return;
    try { setResellerMarkup((await resellerMarkupApi.get(editClient.advertiser_id)).channels); }
    catch { /* keep current */ }
  };
  const addResellerStage = async (channel: string) => {
    if (!editClient?.advertiser_id || resellerBusy) return;
    const d = resellerDraft[channel] || { pct: '', from: '' };
    const pct = parseFloat(d.pct);
    if (isNaN(pct) || pct < 0 || pct > 100) { setSyncResult(t('clients.errMarkupRange')); return; }
    const from = d.from || new Date().toISOString().slice(0, 10);
    setResellerBusy(channel);
    try {
      await resellerMarkupApi.addStage(editClient.advertiser_id, channel, { markup_percent: pct, effective_from: from });
      setResellerDraft({ ...resellerDraft, [channel]: { pct: '', from: '' } });
      await reloadReseller();
    } catch (err: any) {
      setSyncResult(`${t('clients.errReseller')} ${err.response?.data?.detail || err.message}`);
    } finally { setResellerBusy(null); }
  };
  const deleteResellerStage = async (channel: string, stageId: number) => {
    if (!editClient?.advertiser_id || resellerBusy) return;
    setResellerBusy(channel);
    try {
      await resellerMarkupApi.deleteStage(editClient.advertiser_id, channel, stageId);
      await reloadReseller();
    } catch (err: any) {
      setSyncResult(`${t('clients.errReseller')} ${err.response?.data?.detail || err.message}`);
    } finally { setResellerBusy(null); }
  };

  // Per-client commission: set a client-level rate (overrides the agency) or remove it (inherit).
  const reloadClientComm = async () => {
    if (!editClient?.advertiser_id) return;
    try { setClientComm(await commissionsApi.getEntityCommission('advertiser', editClient.advertiser_id)); }
    catch { /* keep current */ }
  };
  const addClientComm = async () => {
    if (!editClient?.advertiser_id || clientCommBusy) return;
    const pct = parseFloat(clientCommDraft.pct);
    if (isNaN(pct) || pct < 0 || pct > 50) { setSyncResult(t('clients.errCommissionRange')); return; }
    const from = clientCommDraft.from || new Date().toISOString().slice(0, 10);
    setClientCommBusy(true);
    try {
      await commissionsApi.addEntityStage('advertiser', editClient.advertiser_id, { commission_percent: pct, effective_from: from });
      setClientCommDraft({ pct: '', from: '' });
      await reloadClientComm();
    } catch (err: any) {
      setSyncResult(`${t('clients.errCommission')} ${err.response?.data?.detail || err.message}`);
    } finally { setClientCommBusy(false); }
  };
  const deleteClientComm = async (stageId: number) => {
    if (!editClient?.advertiser_id || clientCommBusy) return;
    setClientCommBusy(true);
    try {
      await commissionsApi.deleteEntityStage('advertiser', editClient.advertiser_id, stageId);
      await reloadClientComm();
    } catch (err: any) {
      setSyncResult(`${t('clients.errCommission')} ${err.response?.data?.detail || err.message}`);
    } finally { setClientCommBusy(false); }
  };
  // Per-client Sense minimum share: save a client value (0–35%) or clear it (inherit/default).
  const reloadSenseMin = async () => {
    if (!editClient?.advertiser_id) return;
    try { const sm = await commissionsApi.getSenseMin('advertiser', editClient.advertiser_id); setSenseMin(sm); setSenseMinDraft(sm.own != null ? String(sm.own) : ''); }
    catch { /* keep current */ }
  };
  const saveSenseMin = async () => {
    if (!editClient?.advertiser_id || senseMinBusy) return;
    const pct = parseFloat(senseMinDraft);
    if (isNaN(pct) || pct < 0 || pct > 35) { setSyncResult(t('clients.errSenseMinRange', 'Sense minimum must be 0–35%')); return; }
    setSenseMinBusy(true);
    try {
      await commissionsApi.setSenseMin('advertiser', editClient.advertiser_id, pct);
      await reloadSenseMin();
    } catch (err: any) {
      setSyncResult(`${t('clients.errSenseMin', 'Failed to set Sense minimum')} ${err.response?.data?.detail || err.message}`);
    } finally { setSenseMinBusy(false); }
  };
  const clearSenseMin = async () => {
    if (!editClient?.advertiser_id || senseMinBusy) return;
    setSenseMinBusy(true);
    try {
      await commissionsApi.clearSenseMin('advertiser', editClient.advertiser_id);
      await reloadSenseMin();
    } catch (err: any) {
      setSyncResult(`${t('clients.errSenseMin', 'Failed to clear Sense minimum')} ${err.response?.data?.detail || err.message}`);
    } finally { setSenseMinBusy(false); }
  };

  const fmtCid = (id: string) =>
    id.length === 10 ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id;
  const fmtAcc = (kind: 'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin' | 'meta_page' | 'tiktok_identity' | 'linkedin_org', id: string) =>
    kind === 'meta' ? `act_${id}` : kind === 'google' ? fmtCid(id) : id;

  // Load the accounts the connected login can access (for the picker), per platform.
  const loadExternalAccounts = async (kind: 'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin' | 'meta_page' | 'tiktok_identity' | 'linkedin_org', advId?: string) => {
    const adv = advId || editClient?.advertiser_id;
    if (!adv) return;
    const paths: Record<string, string> = {
      google: 'google-ads/customers', meta: 'meta/accounts',
      tiktok: 'tiktok/accounts', yandex: 'yandex/accounts', linkedin: 'linkedin/accounts',
      meta_page: 'meta/pages', tiktok_identity: 'tiktok/identities', linkedin_org: 'linkedin/organizations',
    };
    const url = `/api/oauth/${paths[kind]}?advertiser_id=${adv}`;
    // Open the picker immediately with a loader so it's clear something's happening.
    setGAccounts([]);
    setPickerKind(null);
    setPickerSearch('');
    setPickerShowAll(false);
    setPickerError(null);
    setGLoading(true);
    setPickerKind(kind);
    try {
      const resp = await api.get(url);
      const d = resp.data || {};
      // Ad-account pickers return {customers}; Meta pages {pages}; TikTok identities {identities}; LinkedIn orgs {organizations}.
      const list = kind === 'tiktok_identity'
        ? (d.identities || []).map((i: any) => ({ id: i.id, name: i.name ? `${i.name}${i.type ? ` (${i.type})` : ''}` : i.id, type: i.type, rawName: i.name, bc_id: i.bc_id }))
        : (d.customers || d.pages || d.organizations || []);
      setGAccounts(list);
    } catch (err: any) {
      // Keep the modal open and show the reason inside it, rather than flashing shut.
      setPickerError(err.response?.data?.detail || err.message || t('clients.errPickerLoad'));
    } finally {
      setGLoading(false);
    }
  };

  // Bind the picked account/identity to this client, persist, and chain to the
  // sender picker (Page/identity) after an ad-account pick.
  const chooseAccount = async (a: typeof gAccounts[number]) => {
    const patch: any = pickerKind === 'google' ? { google_ads_customer_id: a.id, google_login_customer_id: (a as any).login_customer_id || '' }
      : pickerKind === 'meta' ? { meta_ad_account_id: `act_${a.id}` }
        : pickerKind === 'tiktok' ? { tiktok_advertiser_id: a.id }
          : pickerKind === 'yandex' ? { yandex_client_login: a.id }
            : pickerKind === 'meta_page' ? { meta_page_id: a.id, meta_page_name: a.name || '' }
              : pickerKind === 'tiktok_identity' ? { tiktok_identity_id: a.id, tiktok_identity_type: a.type || '', tiktok_identity_name: a.rawName || '', tiktok_identity_bc_id: a.bc_id || '' }
                : pickerKind === 'linkedin_org' ? { linkedin_org_id: a.id, linkedin_org_name: a.name || '' }
                  : { linkedin_ad_account_id: a.id };
    const chainKind = pickerKind;
    setEditForm({ ...editForm, ...patch });
    setPickerKind(null);
    if (editClient) {
      try { await clientsApi.update(editClient.advertiser_id, patch); await fetchClients(); }
      catch { /* form still holds it; Save is the fallback */ }
    }
    if (chainKind === 'meta') { setTimeout(() => loadExternalAccounts('meta_page'), 50); }
    else if (chainKind === 'tiktok') { setTimeout(() => loadExternalAccounts('tiktok_identity'), 50); }
    else if (chainKind === 'linkedin') { setTimeout(() => loadExternalAccounts('linkedin_org'), 50); }
  };

  const handleExternalSync = async () => {
    if (!editClient?.advertiser_id) return;
    setSyncRunning(true);
    setSyncResult(null);
    try {
      // Save current IDs first so the sync uses what's in the form
      await clientsApi.update(editClient.advertiser_id, {
        google_ads_customer_id: editForm.google_ads_customer_id.trim() || null,
        meta_ad_account_id: editForm.meta_ad_account_id.trim() || null,
      });
      const res = await clientsApi.triggerExternalSync(editClient.advertiser_id);
      const lines = res.results.map(
        (r) =>
          t('clients.syncLine', {
            source: r.source,
            fetched: r.external_campaigns_fetched,
            matched: r.matched_sense_campaigns,
            rows: r.rows_written,
          }) +
          (r.unmatched_external_names.length
            ? ' ' + t('clients.syncUnmatched', {
              names: r.unmatched_external_names.slice(0, 5).join(', ')
                + (r.unmatched_external_names.length > 5 ? '…' : ''),
            })
            : '')
      );
      setSyncResult(lines.join('\n') || t('clients.noData'));
      setExternalSynced(new Date().toISOString());
    } catch (err: any) {
      setSyncResult(`${t('clients.errorLabel')} ${err.response?.data?.detail || err.message}`);
    } finally {
      setSyncRunning(false);
    }
  };

  const reloadClickAdjs = async (advertiserId: string) => {
    const detail = await clientsApi.get(advertiserId);
    setClickAdjs((detail as any).click_adjustments || []);
  };

  const handleAddClickAdj = async () => {
    if (!editClient?.advertiser_id) return;
    const factor = parseFloat(clickFactorInput.trim().replace(',', '.'));
    if (!factor || factor <= 0) { setClickAdjError(t('clients.errFactor')); return; }
    if (!clickFromInput) { setClickAdjError(t('clients.errFactorFrom')); return; }
    if (!clickReasonInput.trim()) { setClickAdjError(t('clients.errFactorBasis')); return; }
    setClickAdjBusy(true);
    setClickAdjError('');
    try {
      await clientsApi.setClickAdjustment(editClient.advertiser_id, {
        effective_from: clickFromInput, factor, reason: clickReasonInput.trim(),
      });
      await reloadClickAdjs(editClient.advertiser_id);
      setClickFactorInput(''); setClickFromInput(''); setClickReasonInput('');
    } catch (e: any) {
      setClickAdjError(e?.response?.data?.detail || t('clients.errSavePeriod'));
    } finally {
      setClickAdjBusy(false);
    }
  };

  const handleDeleteClickAdj = async (effectiveFrom: string) => {
    if (!editClient?.advertiser_id) return;
    setClickAdjBusy(true);
    setClickAdjError('');
    try {
      await clientsApi.deleteClickAdjustment(editClient.advertiser_id, effectiveFrom);
      await reloadClickAdjs(editClient.advertiser_id);
    } catch (e: any) {
      setClickAdjError(e?.response?.data?.detail || t('clients.errRemovePeriod'));
    } finally {
      setClickAdjBusy(false);
    }
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editClient?.advertiser_id) return;
    setSaving(true);
    setEditError('');
    try {
      const data: any = {
        display_name: editForm.display_name.trim(),
      };
      // Don't send discount/markup for self-service clients (they use auto discount)
      if (!editClient.is_self_service) {
        data.discount_percent = editForm.discount_percent;
        data.client_markup = editForm.client_markup;
      }
      // Billing model for direct clients only. Gate on the agency SELECTED in
      // the form (not the client as loaded): a client assigned to an agency
      // inherits the agency's model, so sending 'prepaid' here would overwrite
      // the inherited postpaid on the backend.
      if (!editForm.agency_id && !editClient.has_freelancer && !editClient.is_self_service && (isAdmin || isRegionalAdmin)) {
        data.billing_model = editForm.billing_model;
      }
      // Only include fields the role is allowed to change
      if (!isFreelancer) {
        data.regional_office_id = editForm.regional_office_id;
        // Managers belong to the office, so moving the client empties the team
        data.manager_ids = editForm.regional_office_id !== editClient.regional_office_id
          ? [] : editForm.manager_ids;
        data.agency_id = editForm.agency_id;
      }
      // External platform cabinet IDs (admin only on the backend)
      if (isAdmin) {
        data.google_ads_customer_id = editForm.google_ads_customer_id.trim() || null;
        data.meta_ad_account_id = editForm.meta_ad_account_id.trim() || null;
        data.tiktok_advertiser_id = editForm.tiktok_advertiser_id.trim() || null;
        data.yandex_client_login = editForm.yandex_client_login.trim() || null;
        data.linkedin_ad_account_id = editForm.linkedin_ad_account_id.trim() || null;
        // Send the Page too, so Save re-checks that ads can actually run from it.
        // It used to travel only at the moment of picking, which meant a client
        // bound to an unusable Page never got told on any later save.
        data.meta_page_id = editForm.meta_page_id.trim() || null;
        data.meta_page_name = editForm.meta_page_name.trim() || null;
        // EU DSA transparency defaults (Meta requires them for EU-targeted ad sets).
        data.meta_dsa_beneficiary = editForm.meta_dsa_beneficiary.trim() || null;
        data.meta_dsa_payor = editForm.meta_dsa_payor.trim() || null;
      }
      // Legal requisites — only when the client has no agency (it's its own buyer).
      if (!editForm.agency_id) {
        data.legal_name = editForm.legal_name.trim();
        data.tax_id = editForm.tax_id.trim();
        data.legal_address = editForm.legal_address.trim();
        data.contract_number = editForm.contract_number.trim();
      }
      const res: any = await clientsApi.update(editClient.advertiser_id, data);
      // Saved, but something still blocks the platform — a Meta Page without the
      // Advertiser role, an ad account not shared yet. Keep the modal open and say
      // so: this used to reach nobody until a launch rolled a whole campaign back.
      const warnings: string[] = res?.warnings || [];
      setShowEditModal(false);
      setEditClient(null);
      await fetchClients();
      if (warnings.length) setSavedWarnings(warnings);
    } catch (err: any) {
      setEditError(err.response?.data?.detail || t('clients.errUpdate'));
    } finally {
      setSaving(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, field?: string) => {
    navigator.clipboard.writeText(text);
    if (field) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('clients.title')}</h1>
          <p className="header-subtitle">{t('clients.subtitle')}</p>
        </div>
        {(isAgencyAdmin || isFreelancer) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
            {showMarkupEditor ? (
              <>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t('clients.defaultMarkup')}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={defaultClientMarkupInput}
                  onChange={(e) => setDefaultClientMarkupInput(e.target.value)}
                  style={{ width: '70px', padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--border-color, #e0e0e0)', fontSize: '13px' }}
                  autoFocus
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>%</span>
                <button
                  className="offices-btn offices-btn-primary"
                  style={{ padding: '5px 14px', fontSize: '12px' }}
                  onClick={async () => { await handleSaveDefaultMarkup(); setShowMarkupEditor(false); }}
                  disabled={savingDefaultMarkup}
                >
                  {savingDefaultMarkup ? '...' : t('common.save')}
                </button>
                <button
                  className="offices-btn offices-btn-secondary"
                  style={{ padding: '5px 10px', fontSize: '12px' }}
                  onClick={() => { setDefaultClientMarkupInput(String(defaultClientMarkup)); setShowMarkupEditor(false); }}
                >
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <button
                className="offices-btn offices-btn-secondary"
                style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={() => setShowMarkupEditor(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                {t('clients.editMarkup')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)' }}>
          <strong>{t('clients.errorLabel')}</strong> {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* New client credentials notification */}
      {newClientCredentials && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#2ecc71', background: 'rgba(46, 204, 113, 0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <strong>{t('clients.createdOk')}</strong>
              <div style={{ marginTop: '12px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{t('clients.loginColon')}</span>{' '}
                  <code style={{ background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: '4px' }}>{newClientCredentials.email}</code>
                  <button
                    onClick={() => copyToClipboard(newClientCredentials.email, 'new-login')}
                    style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'new-login' ? '#2ecc71' : 'var(--accent-orange)' }}
                  >
                    {copiedField === 'new-login' ? t('clients.copied') : t('clients.copy')}
                  </button>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)' }}>{t('clients.passwordColon')}</span>{' '}
                  <code style={{ background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: '4px' }}>{newClientCredentials.password}</code>
                  <button
                    onClick={() => copyToClipboard(newClientCredentials.password, 'new-password')}
                    style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'new-password' ? '#2ecc71' : 'var(--accent-orange)' }}
                  >
                    {copiedField === 'new-password' ? t('clients.copied') : t('clients.copy')}
                  </button>
                </div>
              </div>
              <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                {t('clients.shareCreds')}
              </p>
            </div>
            <button onClick={() => setNewClientCredentials(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>✕</button>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="reports-layout">
        {/* Filters Panel */}
        <aside className="filters-panel">
          {/* Tabs */}
          <div className="filter-section">
            <h3 className="section-heading">{t('clients.status')}</h3>
            <div className="status-segmented" style={{ width: '100%' }}>
              <button
                className={`status-segment ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => setActiveTab('active')}
              >
                {t('clients.tabActive')}
              </button>
              <button
                className={`status-segment ${activeTab === 'paused' ? 'active' : ''}`}
                onClick={() => setActiveTab('paused')}
              >
                {t('clients.tabPaused')}
              </button>
              <button
                className={`status-segment ${activeTab === 'archived' ? 'active' : ''}`}
                onClick={() => setActiveTab('archived')}
              >
                {t('clients.tabArchived')}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('clients.searchByName')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Office filter - only for admin (not freelancer) */}
          {isAdmin && !isFreelancer && (
            <div className="filter-section">
              <h3 className="section-heading">{t('clients.regionalOffice')}</h3>
              <select
                className="form-select"
                value={officeFilter}
                onChange={(e) => {
                  setOfficeFilter(e.target.value);
                  setShowUnassigned(false);
                }}
              >
                <option value="">{t('clients.allOffices')}</option>
                {offices.map(office => (
                  <option key={office.id} value={office.id}>{office.name}</option>
                ))}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showUnassigned}
                  onChange={(e) => {
                    setShowUnassigned(e.target.checked);
                    if (e.target.checked) setOfficeFilter('');
                  }}
                />
                {t('clients.onlyUnassigned')}
              </label>
            </div>
          )}

          {/* Agency filter - for admin and regional_admin (not freelancer) */}
          {(isAdmin || isRegionalAdmin) && !isFreelancer && agencies.length > 0 && (
            <div className="filter-section">
              <h3 className="section-heading">{t('clients.agency')}</h3>
              <select
                className="form-select"
                value={agencyFilter}
                onChange={(e) => setAgencyFilter(e.target.value)}
              >
                <option value="">{t('clients.allAgencies')}</option>
                {agencies.map(agency => (
                  <option key={agency.id} value={agency.id}>{agency.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Create button */}
          <div className="filter-section" style={{ marginTop: 'auto' }}>
            <button className="btn-primary" style={{ width: '100%' }} onClick={() => setShowCreateModal(true)}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18, marginRight: 8 }}>
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              {t('clients.createClient')}
            </button>
          </div>
        </aside>

        {/* Results Area */}
        <div className="results-area visible">
          {loading ? (
            <div className="users-loading">
              <div className="users-spinner" />
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table clients-table">
                <thead>
                  <tr>
                    <th>{t('clients.colClient')}</th>
                    {!isAgencyAdmin && !isAgencyManager && !isFreelancer && <th>{t('clients.colOffice')}</th>}
                    <th>{t('clients.colManager')}</th>
                    <th>{t('clients.colCampaigns')}</th>
                    <th>Max</th>
                    {(isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && <th>{t('clients.colDiscount')}</th>}
                    {(isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && <th>{t('clients.colCommission')}</th>}
                    {(isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && <th>{t('clients.colAccounts')}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan={(isAgencyAdmin || isAgencyManager || isFreelancer) ? 8 : 10} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        {t('clients.noClients')}
                      </td>
                    </tr>
                  ) : (
                    clients.map(client => (
                      <tr key={client.id}>
                        <td>
                          <div className="users-user-cell">
                            <div className="users-avatar" style={{ background: activeTab === 'archived' ? 'linear-gradient(135deg, #999 0%, #777 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                              {client.display_name.substring(0, 2).toUpperCase()}
                            </div>
                            <div className="users-user-info">
                              <span className="users-user-name">{client.display_name}</span>
                              <span className="users-user-email">
                                {`ID: ${client.id}`}
                                {client.creation_status === 'pending' && (
                                  <span
                                    className="clients-agency-badge"
                                    style={{ background: '#fff7ed', color: '#c2410c' }}
                                    title={t('clients.settingUpTitle')}
                                  >{t('clients.settingUp')}</span>
                                )}
                                {client.creation_status === 'error' && (
                                  <span
                                    className="clients-agency-badge"
                                    style={{ background: '#fef2f2', color: '#991b1b' }}
                                    title={t('clients.setupFailedTitle')}
                                  >{t('clients.setupFailed')}</span>
                                )}
                                {client.is_self_service && <span className="clients-agency-badge" style={{background: '#f0f0f0', color: '#666'}}>{t('clients.selfService')}</span>}
                                {client.agency_name && <span className="clients-agency-badge">{client.agency_name}</span>}
                                {client.freelancer_user && <span className="clients-agency-badge" style={{background: '#e8f5e9', color: '#2e7d32'}}>{client.freelancer_user.first_name && client.freelancer_user.last_name ? `${client.freelancer_user.last_name} ${client.freelancer_user.first_name.charAt(0)}.` : client.freelancer_user.email}</span>}
                              </span>
                            </div>
                          </div>
                        </td>
                        {!isAgencyAdmin && !isAgencyManager && !isFreelancer && (
                        <td>
                          {isAdmin && activeTab !== 'archived' ? (
                            <select
                              className="form-select"
                              value={client.regional_office_id || ''}
                              onChange={(e) => handleUpdateOffice(client, e.target.value ? parseInt(e.target.value) : null)}
                              style={{ minWidth: '140px' }}
                            >
                              {(() => {
                                const hub = offices.find(o => o.is_hub) || offices.find(o => o.name === 'Central Hub');
                                return hub ? <option value={hub.id}>{hub.name}</option> : <option value="">Central Hub</option>;
                              })()}
                              {offices.filter(o => !o.is_hub && o.name !== 'Central Hub').map(office => (
                                <option key={office.id} value={office.id}>{office.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span>{client.regional_office_name}</span>
                          )}
                        </td>
                        )}
                        <td>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                            {client.manager_user
                              ? (client.manager_user.last_name
                                ? `${client.manager_user.last_name} ${client.manager_user.first_name ? client.manager_user.first_name.charAt(0) + '.' : ''}`.trim()
                                : client.manager_user.email)
                              : '—'}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{client.campaigns_count}</span>
                        </td>
                        <td>
                          {(client.external_platforms && client.external_platforms.length > 0) ? (
                            <div
                              style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                              title={t('clients.maxReady', { count: client.external_platforms.length })}
                            >
                              {MAX_PLATFORM_META.filter(p => (yandexOffered || p.key !== 'yandex') && client.external_platforms!.includes(p.key)).map(p => (
                                <span
                                  key={p.key}
                                  title={p.name}
                                  style={{
                                    width: 20, height: 20, borderRadius: 5, background: p.color, color: '#fff',
                                    fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center',
                                    justifyContent: 'center', lineHeight: 1,
                                  }}
                                >
                                  {p.label}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }} title={t('clients.maxNone')}>—</span>
                          )}
                        </td>
                        {(isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && (
                        <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                          {client.discount_percent > 0 ? (
                            <span className="discount-badge discount-manual">{client.discount_percent}%</span>
                          ) : '—'}
                        </td>
                        )}
                        {(isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && (
                        <td style={{ textAlign: 'center' }}>
                          {(() => {
                            const cm = commissionMap[client.id];
                            if (!cm) return '—';
                            return (
                              <span className={`discount-badge ${cm.source === 'auto' ? 'discount-auto' : 'discount-manual'}`}>
                                {cm.commission_percent}%{' '}
                                {cm.source === 'auto' ? t('clients.sourceAuto') : t('clients.sourceManual')}
                              </span>
                            );
                          })()}
                        </td>
                        )}
                        {(isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && (
                        <td style={{ textAlign: 'center' }}>
                          {(() => {
                            const cm = commissionMap[client.id];
                            if (!cm) return '—';
                            const own = cm.model_type === 'own_accounts';
                            return (
                              <span
                                title={own ? t('clients.ownAccountsBadge') : t('clients.clientAccountsBadge')}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, fontSize: 12, fontWeight: 800, color: '#fff', background: own ? '#7c3aed' : '#059669' }}
                              >
                                {own ? 'S' : 'C'}
                              </span>
                            );
                          })()}
                        </td>
                        )}
                        <td>
                          <div className="users-actions">
                            {activeTab !== 'archived' && (
                              <button
                                className="users-action-btn"
                                onClick={() => openEditModal(client)}
                                title={t('clients.actEdit')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                            )}
                            {client.client_user && (
                              <button
                                className="users-action-btn"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setNewPassword('');
                                  setShowPassword(false);
                                  setShowCredentialsModal(true);
                                }}
                                title={t('clients.actCreds')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                              </button>
                            )}
                            {activeTab === 'archived' ? (
                              <button
                                className="users-action-btn users-action-activate"
                                onClick={() => {
                                  setArchiveClient(client);
                                  setArchiveAction('restore');
                                  setShowArchiveModal(true);
                                }}
                                title={t('clients.actRestore')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M23 4v6h-6"/>
                                  <path d="M1 20v-6h6"/>
                                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                </svg>
                              </button>
                            ) : null}
                            {activeTab === 'archived' && isAdmin && (
                              <button
                                className="users-action-btn users-action-delete"
                                onClick={() => {
                                  setDeleteClient(client);
                                  setShowDeleteModal(true);
                                }}
                                title={t('clients.actDelete')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            )}
                            {activeTab !== 'archived' && (
                              <button
                                className="users-action-btn users-action-delete"
                                onClick={() => {
                                  setArchiveClient(client);
                                  setArchiveAction('archive');
                                  setShowArchiveModal(true);
                                }}
                                title={t('clients.actArchive')}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="21 8 21 21 3 21 3 8"/>
                                  <rect x="1" y="3" width="22" height="5"/>
                                  <line x1="10" y1="12" x2="14" y2="12"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Client Modal */}
      {showCreateModal && (
        <div className="users-modal-overlay" onClick={() => { setShowCreateModal(false); setCreateError(''); setDomainTouched(false); }}>
          <div className="users-modal" onClick={e => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{t('clients.createClient')}</h2>
              <button className="users-modal-close" onClick={() => { setShowCreateModal(false); setCreateError(''); setDomainTouched(false); }}>&times;</button>
            </div>
            <form onSubmit={handleCreateClient}>
              <div className="users-modal-body">
                {createError && <div className="users-modal-error">{createError}</div>}

                <div className="users-form-field">
                  <label>{t('clients.clientNameLabel')}</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder={t('clients.companyNamePh')}
                    required
                    autoFocus
                  />
                </div>

                <div className="users-form-field">
                  <label>{t('clients.domain')}</label>
                  <input
                    type="text"
                    value={createForm.domain}
                    onChange={(e) => { setCreateForm({ ...createForm, domain: e.target.value }); setDomainTouched(true); }}
                    onBlur={() => setDomainTouched(true)}
                    placeholder="https://company.com"
                    style={domainTouched && createForm.domain && validateDomain(createForm.domain) ? { borderColor: '#e74c3c' } : undefined}
                  />
                  {domainTouched && createForm.domain && validateDomain(createForm.domain) && (
                    <small style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {validateDomain(createForm.domain)}
                    </small>
                  )}
                </div>

                {isAdmin && (
                  <div className="users-form-field">
                    <label>{t('clients.regionalOffice')}</label>
                    <select
                      value={createForm.regional_office_id || ''}
                      onChange={(e) => setCreateForm({ ...createForm, regional_office_id: e.target.value ? parseInt(e.target.value) : null })}
                    >
                      {(() => {
                        const hub = offices.find(o => o.is_hub) || offices.find(o => o.name === 'Central Hub');
                        return hub ? <option value={hub.id}>{hub.name}</option> : <option value="">Central Hub</option>;
                      })()}
                      {offices.filter(o => !o.is_hub && o.name !== 'Central Hub').map(office => (
                        <option key={office.id} value={office.id}>{office.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="users-modal-footer">
                <button type="button" className="users-btn users-btn-secondary" onClick={() => { setShowCreateModal(false); setCreateError(''); setDomainTouched(false); }}>
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="users-btn users-btn-primary"
                  disabled={creating || !createForm.name.trim() || (!!createForm.domain && !!validateDomain(createForm.domain))}
                >
                  {creating ? t('clients.creating') : t('clients.createClient')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive/Restore Confirmation Modal */}
      {showArchiveModal && archiveClient && (
        <div className="users-modal-overlay" onClick={() => { setShowArchiveModal(false); setArchiveClient(null); }}>
          <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="users-modal-header">
              <h2>{t(archiveAction === 'archive' ? 'clients.archiveTitle' : 'clients.restoreTitle')}</h2>
              <button className="users-modal-close" onClick={() => { setShowArchiveModal(false); setArchiveClient(null); }}>&times;</button>
            </div>
            <div className="users-modal-body">
              <p style={{ fontSize: '15px', lineHeight: '1.6' }}>
                <Trans
                  i18nKey={archiveAction === 'archive' ? 'clients.archiveConfirm' : 'clients.restoreConfirm'}
                  values={{ name: archiveClient.display_name }}
                  components={{ b: <strong /> }}
                />
              </p>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn users-btn-secondary" onClick={() => { setShowArchiveModal(false); setArchiveClient(null); }}>
                {t('common.cancel')}
              </button>
              <button
                className={`users-btn ${archiveAction === 'archive' ? 'users-btn-danger' : 'users-btn-primary'}`}
                onClick={handleArchiveRestore}
                disabled={archiving}
                style={archiveAction === 'archive' ? { background: '#e74c3c', color: '#fff' } : undefined}
              >
                {archiving
                  ? t(archiveAction === 'archive' ? 'clients.archiving' : 'clients.restoring')
                  : t(archiveAction === 'archive' ? 'clients.archive' : 'clients.restore')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {savedWarnings && (
        <div className="users-modal-overlay" onClick={() => setSavedWarnings(null)}>
          <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="users-modal-header">
              <h2>{t('clients.savedOneLeft')}</h2>
              <button className="users-modal-close" onClick={() => setSavedWarnings(null)}>&times;</button>
            </div>
            <div className="users-modal-body">
              {savedWarnings.map((w, i) => (
                <p key={i} style={{ fontSize: '15px', lineHeight: '1.6', margin: i ? '12px 0 0' : 0 }}>{w}</p>
              ))}
            </div>
            <div className="users-modal-footer">
              <button className="users-btn users-btn-primary" onClick={() => setSavedWarnings(null)}>{t('clients.gotIt')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {showDeleteModal && deleteClient && (
        <div className="users-modal-overlay" onClick={() => { setShowDeleteModal(false); setDeleteClient(null); }}>
          <div className="users-modal" onClick={e => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{t('clients.deleteTitle')}</h2>
              <button className="users-modal-close" onClick={() => { setShowDeleteModal(false); setDeleteClient(null); }}>&times;</button>
            </div>
            <div className="users-modal-body">
              <p>
                <Trans
                  i18nKey="clients.deleteConfirm"
                  values={{ name: deleteClient.display_name }}
                  components={{ b: <strong />, s: <strong /> }}
                />
              </p>
              <p style={{ fontSize: 12, color: '#6c757d' }}>
                {t('clients.deleteNote')}
              </p>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn users-btn-secondary" onClick={() => { setShowDeleteModal(false); setDeleteClient(null); }}>
                {t('common.cancel')}
              </button>
              <button
                className="users-btn"
                onClick={handleDelete}
                disabled={deleting}
                style={{ background: '#e74c3c', color: '#fff' }}
              >
                {deleting ? t('clients.deleting') : t('clients.deletePermanently')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editClient && (
        <div className="users-modal-overlay" onClick={() => { setShowEditModal(false); setEditClient(null); setEditError(''); }}>
          <div className="users-modal" onClick={e => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{t('clients.editTitle')}</h2>
              <button className="users-modal-close" onClick={() => { setShowEditModal(false); setEditClient(null); setEditError(''); }}>&times;</button>
            </div>
            <div className="users-modal-body">
              {editError && <div className="users-modal-error">{editError}</div>}

              <div className="users-form-field">
                <label>{t('clients.nameLabel')}</label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  placeholder={t('clients.clientNamePh')}
                  autoFocus
                  disabled={editClient.is_self_service}
                />
              </div>

              {/* User (read-only) for self-service clients */}
              {editClient.is_self_service && editClient.client_user && (
                <div className="users-form-field">
                  <label>{t('clients.userLabel')}</label>
                  <input type="text" value={editClient.client_user.email} disabled />
                </div>
              )}

              {!isAgencyAdmin && !isAgencyManager && !isFreelancer && (
              <div className="users-form-field">
                <label>{t('clients.regionalOffice')}</label>
                {isAdmin ? (
                  <select
                    value={editForm.regional_office_id || ''}
                    onChange={async (e) => {
                      const newOfficeId = e.target.value ? parseInt(e.target.value) : null;
                      // Reset discount and markup when changing office
                      setEditForm({ ...editForm, regional_office_id: newOfficeId, manager_ids: [], discount_percent: 0, client_markup: null });
                      try {
                        const data = await clientsApi.getManagers(newOfficeId ?? 0, editForm.agency_id);
                        setManagers(data.managers);
                      } catch { setManagers([]); }
                    }}
                  >
                    {/* Send the real Hub id, not empty string — backend treats null
                        as "leave as-is" and the link silently drops. */}
                    {(() => {
                      const hub = offices.find(o => o.is_hub) || offices.find(o => o.name === 'Central Hub');
                      return hub ? <option value={hub.id}>{hub.name}</option> : <option value="">Central Hub</option>;
                    })()}
                    {offices.filter(o => !o.is_hub && o.name !== 'Central Hub').map(office => (
                      <option key={office.id} value={office.id}>{office.name}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={editClient.regional_office_name} disabled />
                )}
              </div>
              )}

              {!isAgencyAdmin && !isAgencyManager && !isFreelancer && (
              <div className="users-form-field">
                <label>{t('clients.managersSel', { count: editForm.manager_ids.length })}</label>
                {(isAdmin || isRegionalAdmin) ? (
                  <div className="users-advertisers-list">
                    {managers.length === 0 ? (
                      <div className="agencies-clients-empty">{t('clients.noManagers')}</div>
                    ) : managers.map(m => {
                      const selected = editForm.manager_ids.includes(m.id);
                      return (
                        <div
                          key={m.id}
                          className={`users-advertiser-item ${selected ? 'selected' : ''}`}
                          onClick={() => setEditForm({
                            ...editForm,
                            manager_ids: selected
                              ? editForm.manager_ids.filter(id => id !== m.id)
                              : [...editForm.manager_ids, m.id],
                          })}
                        >
                          <span className="users-checkbox-custom">
                            <svg viewBox="0 0 24 24" fill="none">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span className="users-advertiser-name">
                            {m.last_name ? `${m.last_name} ${m.first_name ? m.first_name.charAt(0) + '.' : ''}`.trim() : m.email}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={(editClient.managers || []).map(m => m.email).join(', ') || t('clients.notAssigned')}
                    disabled
                  />
                )}
              </div>
              )}

              {/* Agency — hidden for self-service and freelancer-owned clients */}
              {!editClient.is_self_service && !editClient.has_freelancer && (isAdmin || isRegionalAdmin) && agencies.length > 0 && (
                <div className="users-form-field">
                  <label>{t('clients.agency')}</label>
                  <select
                    value={editForm.agency_id || ''}
                    onChange={(e) => {
                      const agencyId = e.target.value ? parseInt(e.target.value) : null;
                      const agency = agencies.find(a => a.id === agencyId);
                      // Reset discount and markup when changing agency
                      if (agency && agency.regional_office_id && agency.regional_office_id !== editForm.regional_office_id) {
                        setEditForm({ ...editForm, agency_id: agencyId, regional_office_id: agency.regional_office_id, manager_ids: [], discount_percent: 0, client_markup: null });
                      } else {
                        setEditForm({ ...editForm, agency_id: agencyId, discount_percent: 0, client_markup: null });
                      }
                    }}
                  >
                    <option value="">{t('clients.noAgency')}</option>
                    {agencies.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Legal requisites — only for direct/self-service clients (no agency)
                  of a Kazakhstan (KZT) office; the client is then its own buyer. */}
              {!editForm.agency_id && offices.find(o => o.id === editForm.regional_office_id)?.currency === 'KZT' && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{t('clients.legalDetails')}</div>
                  <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 8 }}>
                    {t('clients.legalKzNote')}
                  </div>
                  <RequisitesFields
                    value={{ legal_name: editForm.legal_name, tax_id: editForm.tax_id, legal_address: editForm.legal_address, contract_number: editForm.contract_number }}
                    onChange={(r) => setEditForm({ ...editForm, legal_name: r.legal_name, tax_id: r.tax_id, legal_address: r.legal_address, contract_number: r.contract_number })}
                  />
                </div>
              )}

              {/* Discount — for all non-self-service clients, editable by admin/regional_admin/agency_admin/freelancer */}
              {!editClient.is_self_service && (isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && (
                <div className="users-form-field">
                  <label>{t('clients.discountPct')}</label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={editForm.discount_percent}
                    onChange={(e) => setEditForm({ ...editForm, discount_percent: Math.min(parseFloat(e.target.value) || 0, 50) })}
                  />
                </div>
              )}

              {/* Client Markup — only for clients under agency/freelancer */}
              {!editClient.is_self_service && (editClient.agency_id || editClient.has_freelancer) && (isAdmin || isRegionalAdmin || isAgencyAdmin || isFreelancer) && (
                <div className="users-form-field">
                  <label>{t('clients.clientMarkupPct')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={editForm.client_markup ?? ''}
                      placeholder={t('clients.markupDefaultPh', { pct: defaultClientMarkup })}
                      onChange={(e) => setEditForm({ ...editForm, client_markup: e.target.value !== '' ? parseFloat(e.target.value) || 0 : null })}
                    />
                    {editForm.client_markup !== null && (
                      <button
                        type="button"
                        className="offices-btn offices-btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => setEditForm({ ...editForm, client_markup: null })}
                      >
                        {t('clients.resetToDefault')}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* Billing Model — for direct clients (not agency/freelancer/self-service), admin/regional_admin only */}
              {editClient && isDirectClient(editClient) && (isAdmin || isRegionalAdmin) && (
                <div className="users-form-field">
                  <label>{t('clients.billingModel')}</label>
                  <select
                    value={editForm.billing_model}
                    onChange={(e) => setEditForm({ ...editForm, billing_model: e.target.value })}
                  >
                    <option value="prepaid">{t('clients.prepaid')}</option>
                    <option value="postpaid">{t('clients.postpaid')}</option>
                  </select>
                </div>
              )}

              {/* Click reconciliation — administrators only. Moves the delivery
                  numbers this client reads, so it is deliberately out of reach of
                  office and agency admins, and never shown to anyone else. */}
              {isAdmin && editClient && (
                <div
                  style={{
                    marginTop: 12, padding: 14, borderRadius: 10,
                    border: '1px solid #e5e7eb', background: '#fafafa',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{t('clients.clickRecon')}</div>
                  <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
                    {t('clients.clickReconNote')}
                  </div>

                  {clickAdjs.length > 0 && (
                    <table style={{ width: '100%', fontSize: 12, marginBottom: 10, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: '#6c757d', textAlign: 'left' }}>
                          <th style={{ padding: '4px 6px', fontWeight: 500 }}>{t('clients.colFrom')}</th>
                          <th style={{ padding: '4px 6px', fontWeight: 500 }}>{t('clients.colFactor')}</th>
                          <th style={{ padding: '4px 6px', fontWeight: 500 }}>{t('clients.colBasis')}</th>
                          <th style={{ width: 28 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {clickAdjs.map((adj) => (
                          <tr key={adj.effective_from} style={{ borderTop: '1px solid #eceff1' }}>
                            <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{adj.effective_from}</td>
                            <td style={{ padding: '5px 6px', fontWeight: 600 }}>
                              ×{adj.factor} <span style={{ color: '#6c757d', fontWeight: 400 }}>
                                ({adj.factor >= 1 ? '+' : ''}{Math.round((adj.factor - 1) * 100)}%)
                              </span>
                            </td>
                            <td style={{ padding: '5px 6px', color: '#6c757d' }} title={adj.updated_by_email || ''}>
                              {adj.reason}
                            </td>
                            <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={() => handleDeleteClickAdj(adj.effective_from)}
                                disabled={clickAdjBusy}
                                title={t('clients.removePeriod')}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 15, lineHeight: 1, padding: 0 }}
                              >×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="users-form-field" style={{ flex: '0 0 150px', marginBottom: 0 }}>
                      <label>{t('clients.colFrom')}</label>
                      <input type="date" value={clickFromInput} onChange={(e) => setClickFromInput(e.target.value)} />
                    </div>
                    <div className="users-form-field" style={{ flex: '0 0 100px', marginBottom: 0 }}>
                      <label>{t('clients.colFactor')}</label>
                      <input
                        type="text" inputMode="decimal" placeholder="1.20"
                        value={clickFactorInput} onChange={(e) => setClickFactorInput(e.target.value)}
                      />
                    </div>
                    <div className="users-form-field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                      <label>{t('clients.colBasis')}</label>
                      <input
                        type="text" placeholder={t('clients.basisPh')}
                        value={clickReasonInput} onChange={(e) => setClickReasonInput(e.target.value)}
                      />
                    </div>
                    <button
                      type="button" className="btn-secondary"
                      onClick={handleAddClickAdj} disabled={clickAdjBusy}
                      style={{ height: 36 }}
                    >
                      {clickAdjBusy ? t('clients.savingShort') : t('clients.addPeriod')}
                    </button>
                  </div>

                  {clickAdjError && (
                    <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>{clickAdjError}</div>
                  )}
                </div>
              )}

              {/* Advertiser creation status — visible when not complete, admin can retry */}
              {isAdmin && editClient && editClient.creation_status && editClient.creation_status !== 'complete' && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 14,
                    border: `1px solid ${editClient.creation_status === 'error' ? '#fecaca' : '#fed7aa'}`,
                    background: editClient.creation_status === 'error' ? '#fef2f2' : '#fff7ed',
                    borderRadius: 10,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: editClient.creation_status === 'error' ? '#991b1b' : '#9a3412' }}>
                    {t(editClient.creation_status === 'error' ? 'clients.advSetupFailed' : 'clients.advSettingUp')}
                  </div>
                  <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 8 }}>
                    <Trans
                      i18nKey="clients.blockedUntilSetup"
                      values={{ status: editClient.creation_status }}
                      components={{ b: <b /> }}
                    />
                  </div>
                  {editClient.creation_status === 'error' && (
                    <div style={{ fontSize: 12, color: '#991b1b', marginBottom: 8 }}>
                      {t('clients.couldntSetup')}
                    </div>
                  )}
                  <button
                    type="button"
                    className="users-btn users-btn-secondary"
                    disabled={retryingCreation}
                    onClick={async () => {
                      if (!editClient?.advertiser_id) return;
                      setRetryingCreation(true);
                      try {
                        const r = await clientsApi.retryCreation(editClient.advertiser_id);
                        if (r.success && r.advertiser_id) {
                          // New real id was assigned — refresh list and close modal.
                          await fetchClients();
                          setShowEditModal(false);
                          setEditClient(null);
                        } else {
                          // Refresh local view with new status/error.
                          setEditClient({
                            ...editClient,
                            advertiser_id: r.advertiser_id || editClient.advertiser_id,
                            creation_status: r.creation_status || editClient.creation_status,
                            creation_error: r.creation_error ?? null,
                          });
                        }
                      } catch (e: any) {
                        alert(t('clients.couldntSetupAlert'));
                      } finally {
                        setRetryingCreation(false);
                      }
                    }}
                  >
                    {retryingCreation ? t('clients.retrying') : t('clients.retryCreation')}
                  </button>
                </div>
              )}

              {/* External platforms (Google Ads + Meta) — admin only */}
              {isAdmin && (
                <div style={{ marginTop: 12, padding: 14, border: '1px solid var(--border-color, #e5e5ea)', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{t('clients.externalPlatforms')}</div>
                  {ownership && (
                    <div className="users-form-field" style={{ marginBottom: 14 }}>
                      <label>{t('clients.whoseAccounts')}</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {[{ v: 'own_accounts', labelKey: 'clients.ourAccounts' },
                          { v: 'client_accounts', labelKey: 'clients.clientAccounts' }].map(o => (
                          <button
                            key={o.v}
                            type="button"
                            className={`users-btn ${ownership.model_type === o.v ? 'users-btn-primary' : 'users-btn-secondary'}`}
                            disabled={ownershipBusy || ownership.model_type === o.v}
                            onClick={() => applyOwnership(o.v)}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {t(o.labelKey)}
                          </button>
                        ))}
                        {ownership.is_override && (
                          <button type="button" className="users-btn users-btn-secondary" disabled={ownershipBusy}
                            onClick={resetOwnership} title={t('clients.revertModelTitle')}
                            style={{ whiteSpace: 'nowrap' }}>
                            {t('clients.resetToDefault')}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary, #8a8a8e)', marginTop: 6 }}>
                        {t(ownership.model_type === 'own_accounts'
                          ? 'clients.ourAccountsNote'
                          : 'clients.clientAccountsNote')}
                        {' · '}
                        {ownership.is_override
                          ? t('clients.modelOverridden')
                          : t('clients.modelInherited', {
                            entity: t(`clients.entity_${ownership.inherited.entity_type}`,
                              { defaultValue: ownership.inherited.entity_type }),
                          })}
                      </div>
                    </div>
                  )}
                  {/* Per-client commission (АК). A client rate set here is the highest priority and
                      overrides the agency; with none set the client inherits the agency's rate. The
                      account-model toggle above never changes this rate. */}
                  {clientComm && (
                    <div className="users-form-field" style={{ marginBottom: 14 }}>
                      <label>{t('clients.commissionPct')}</label>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary, #8a8a8e)', marginBottom: 8 }}>
                        {t('clients.commissionNote')}
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        {t('clients.currentLabel')} <b>{clientComm.current_commission?.commission_percent}%</b>{' '}
                        <span style={{ color: 'var(--text-secondary, #8a8a8e)' }}>
                          ({clientComm.current_commission?.source === 'auto'
                            ? t('clients.sourceAuto')
                            : clientComm.current_commission?.level === 'advertiser'
                              ? t('clients.setForThisClient')
                              : t('clients.inheritedFromLevel', {
                                level: t(`clients.entity_${clientComm.current_commission?.level || 'agency'}`,
                                  { defaultValue: clientComm.current_commission?.level || 'agency' }),
                              })})
                        </span>
                      </div>
                      {clientComm.stages && clientComm.stages.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          {clientComm.stages.map((s: any) => (
                            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                              color: s.is_active ? 'inherit' : 'var(--text-secondary, #8a8a8e)' }}>
                              <span style={{ minWidth: 48 }}>{s.commission_percent}%</span>
                              <span>{t('clients.stageFrom', { date: s.effective_from })}{s.is_active ? '' : t('clients.stageScheduled')}</span>
                              <button type="button" className="users-btn users-btn-secondary"
                                disabled={clientCommBusy} onClick={() => deleteClientComm(s.id)}
                                style={{ padding: '1px 8px', color: '#c0392b' }}>{t('clients.remove')}</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="number" min={0} max={50} step={0.1} placeholder="0–50%"
                          value={clientCommDraft.pct}
                          onChange={e => setClientCommDraft({ ...clientCommDraft, pct: e.target.value })}
                          style={{ width: 90 }} />
                        <input type="date" value={clientCommDraft.from}
                          onChange={e => setClientCommDraft({ ...clientCommDraft, from: e.target.value })} />
                        <button type="button" className="users-btn users-btn-primary"
                          disabled={clientCommBusy} onClick={addClientComm}>{t('clients.fix')}</button>
                      </div>
                    </div>
                  )}
                  {/* Sense minimum budget share — the floor the Sense slice can be dragged DOWN to in
                      a manual Sense Max budget split. Per-client (0–35%); empty = inherit agency / default. */}
                  {senseMin && (
                    <div className="users-form-field" style={{ marginBottom: 14 }}>
                      <label>{t('clients.senseMinPct', 'Sense minimum share')}</label>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary, #8a8a8e)', marginBottom: 8 }}>
                        {t('clients.senseMinNote', 'Lowest budget share Sense can be dragged to when splitting a Sense Max campaign manually (0–35%). Leave empty to inherit the agency rate or the default.')}
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        {t('clients.currentLabel')} <b>{senseMin.current?.min_percent}%</b>{' '}
                        <span style={{ color: 'var(--text-secondary, #8a8a8e)' }}>
                          ({senseMin.current?.source === 'client'
                            ? t('clients.setForThisClient')
                            : senseMin.current?.source === 'inherited'
                              ? t('clients.inheritedFromLevel', { level: t(`clients.entity_${senseMin.current?.level || 'agency'}`, { defaultValue: senseMin.current?.level || 'agency' }) })
                              : t('clients.sourceDefault', 'default')})
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="number" min={0} max={35} step={0.5} placeholder="0–35%"
                          value={senseMinDraft}
                          onChange={e => setSenseMinDraft(e.target.value)}
                          style={{ width: 90 }} />
                        <button type="button" className="users-btn users-btn-primary"
                          disabled={senseMinBusy} onClick={saveSenseMin}>{t('clients.save', 'Save')}</button>
                        {senseMin.own != null && (
                          <button type="button" className="users-btn users-btn-secondary"
                            disabled={senseMinBusy} onClick={clearSenseMin} style={{ color: '#c0392b' }}>
                            {t('clients.resetInherit', 'Reset (inherit)')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Reseller markup per platform — only bites on OUR OWN cabinets (own_accounts).
                      Baked INTO the external spend the client/agency sees; АК then applies on top. */}
                  {ownership?.model_type === 'own_accounts' && resellerMarkup && (
                    <div className="users-form-field" style={{ marginBottom: 14 }}>
                      <label>{t('clients.resellerMarkup')}</label>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary, #8a8a8e)', marginBottom: 8 }}>
                        {t('clients.resellerNote')}
                      </div>
                      {([
                        { k: 'google_ads', n: 'Google Ads' }, { k: 'meta', n: 'Meta' }, { k: 'tiktok', n: 'TikTok' },
                        { k: 'yandex', n: 'Yandex' }, { k: 'linkedin', n: 'LinkedIn' },
                        // The backend drops the same two from the markup channels on a
                        // white-label; this list is the UI's own, so it has to drop
                        // them here as well.
                      ] as const)
                        .filter(({ k }) => (yandexOffered || k !== 'yandex')
                          && (linkedinOffered || k !== 'linkedin'))
                        .map(({ k, n }) => {
                        const ch = resellerMarkup[k];
                        const draft = resellerDraft[k] || { pct: '', from: '' };
                        return (
                          <div key={k} style={{ border: '1px solid var(--border-color, #e5e5ea)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{n}</div>
                              <div style={{ fontSize: 13 }}>
                                {t('clients.currentLabel')} <b>{ch ? ch.current_markup : 0}%</b>
                              </div>
                            </div>
                            {ch && ch.stages.length > 0 && (
                              <div style={{ marginTop: 6 }}>
                                {ch.stages.map(s => (
                                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                                    color: s.is_active ? 'inherit' : 'var(--text-secondary, #8a8a8e)' }}>
                                    <span style={{ minWidth: 48 }}>{s.markup_percent}%</span>
                                    <span>{t('clients.stageFrom', { date: s.effective_from })}{s.is_active ? '' : t('clients.stageScheduled')}</span>
                                    <button type="button" className="users-btn users-btn-secondary"
                                      disabled={resellerBusy === k} onClick={() => deleteResellerStage(k, s.id)}
                                      style={{ padding: '1px 8px', color: '#c0392b' }}>{t('clients.remove')}</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                              <input type="number" min={0} max={100} step={0.5} placeholder="%" value={draft.pct}
                                onChange={e => setResellerDraft({ ...resellerDraft, [k]: { ...draft, pct: e.target.value } })}
                                style={{ width: 72, flex: '0 0 auto' }} />
                              <input type="date" value={draft.from}
                                onChange={e => setResellerDraft({ ...resellerDraft, [k]: { ...draft, from: e.target.value } })}
                                title={t('clients.effectiveFromTitle')}
                                style={{ width: 150, flex: '0 0 auto' }} />
                              <button type="button" className="users-btn users-btn-secondary"
                                disabled={resellerBusy === k} onClick={() => addResellerStage(k)}
                                style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>{t('clients.addRate')}</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="users-form-field">
                    <label>{t('clients.googleCid')}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        style={{ flex: 1 }}
                        value={editForm.google_ads_customer_id ? fmtCid(editForm.google_ads_customer_id.replace(/\D/g, '')) : ''}
                        onChange={e => setEditForm({ ...editForm, google_ads_customer_id: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        placeholder={t('clients.googleCidPh')}
                        title={t('clients.googleSetTitle')}
                      />
                      <button
                        type="button"
                        className="users-btn users-btn-secondary"
                        onClick={() => connectExternal('google')}
                        title={t('clients.googleAuthTitle')}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {editForm.google_ads_customer_id ? t('clients.reconnect') : t('clients.connectGoogle')}
                      </button>
                      {editForm.google_ads_customer_id && (
                        <button type="button" className="users-btn users-btn-secondary" onClick={() => disconnectExternal('google')} title={t('clients.disconnectTitle')} style={{ whiteSpace: 'nowrap', color: '#c0392b' }}>{t('clients.disconnect')}</button>
                      )}
                    </div>
                  </div>
                  <div className="users-form-field">
                    <label>{t('clients.metaAccId')}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        style={{ flex: 1 }}
                        value={editForm.meta_ad_account_id}
                        placeholder={t('clients.metaAccPh')}
                        readOnly
                        title={t('clients.metaBindTitle')}
                      />
                      <button
                        type="button"
                        className="users-btn users-btn-secondary"
                        onClick={() => loadExternalAccounts('meta')}
                        title={t('clients.metaPickTitle')}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {editForm.meta_ad_account_id ? t('clients.changeAdAccount') : t('clients.selectAdAccount')}
                      </button>
                      {editForm.meta_ad_account_id && (
                        <button type="button" className="users-btn users-btn-secondary" onClick={() => disconnectExternal('meta')} title={t('clients.disconnectTitle')} style={{ whiteSpace: 'nowrap', color: '#c0392b' }}>{t('clients.disconnect')}</button>
                      )}
                    </div>
                    {editForm.meta_ad_account_id && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 13 }}>
                        <span style={{ color: '#777' }}>{t('clients.adsFromPage')}</span>
                        <span style={{ fontWeight: 600, color: editForm.meta_page_id ? '#1b1430' : '#c0392b' }}>
                          {editForm.meta_page_name || editForm.meta_page_id || t('clients.noneRequired')}
                        </span>
                        <button type="button" className="users-btn users-btn-secondary" style={{ whiteSpace: 'nowrap' }}
                          onClick={() => loadExternalAccounts('meta_page')}>{t('clients.selectPage')}</button>
                      </div>
                    )}
                    {editForm.meta_ad_account_id && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 12, color: '#777' }}>{t('clients.dsaBeneficiary')}</label>
                          <input type="text" style={{ width: '100%' }}
                            value={editForm.meta_dsa_beneficiary}
                            placeholder={t('clients.dsaBeneficiaryPh')}
                            onChange={(e) => setEditForm({ ...editForm, meta_dsa_beneficiary: e.target.value })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 12, color: '#777' }}>{t('clients.dsaPayor')}</label>
                          <input type="text" style={{ width: '100%' }}
                            value={editForm.meta_dsa_payor}
                            placeholder={t('clients.dsaPayorPh')}
                            onChange={(e) => setEditForm({ ...editForm, meta_dsa_payor: e.target.value })} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="users-form-field">
                    <label>{t('clients.tiktokAdvId')}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        style={{ flex: 1 }}
                        value={editForm.tiktok_advertiser_id}
                        placeholder={t('clients.tiktokPh')}
                        readOnly
                        title={t('clients.tiktokSetTitle')}
                      />
                      <button
                        type="button"
                        className="users-btn users-btn-secondary"
                        onClick={() => connectExternal('tiktok')}
                        title={t('clients.tiktokAuthTitle')}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {editForm.tiktok_advertiser_id ? t('clients.reconnect') : t('clients.connectTiktok')}
                      </button>
                      {editForm.tiktok_advertiser_id && (
                        <button type="button" className="users-btn users-btn-secondary" onClick={() => disconnectExternal('tiktok')} title={t('clients.disconnectTitle')} style={{ whiteSpace: 'nowrap', color: '#c0392b' }}>{t('clients.disconnect')}</button>
                      )}
                    </div>
                    {editForm.tiktok_advertiser_id && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 13 }}>
                        <span style={{ color: '#777' }}>{t('clients.adsFromIdentity')}</span>
                        <span style={{ fontWeight: 600, color: editForm.tiktok_identity_id ? '#1b1430' : '#c0392b' }}>
                          {editForm.tiktok_identity_name || editForm.tiktok_identity_id || t('clients.noneRequired')}
                        </span>
                        <button type="button" className="users-btn users-btn-secondary" style={{ whiteSpace: 'nowrap' }}
                          onClick={() => loadExternalAccounts('tiktok_identity')}>{t('clients.selectIdentity')}</button>
                      </div>
                    )}
                  </div>
                  {yandexOffered && (
                    <div className="users-form-field">
                      <label>{t('clients.yandexLogin')}</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="text"
                          style={{ flex: 1 }}
                          value={editForm.yandex_client_login}
                          onChange={e => setEditForm({ ...editForm, yandex_client_login: e.target.value })}
                          placeholder={t('clients.yandexPh')}
                          title={t('clients.yandexTypeTitle')}
                        />
                        <button
                          type="button"
                          className="users-btn users-btn-secondary"
                          onClick={() => connectExternal('yandex')}
                          title={t('clients.yandexAuthTitle')}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {editForm.yandex_client_login ? t('clients.reconnect') : t('clients.connectYandex')}
                        </button>
                        {editForm.yandex_client_login && (
                          <button type="button" className="users-btn users-btn-secondary" onClick={() => disconnectExternal('yandex')} title={t('clients.disconnectTitle')} style={{ whiteSpace: 'nowrap', color: '#c0392b' }}>{t('clients.disconnect')}</button>
                        )}
                      </div>
                      <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                        {t('clients.yandexNote')}
                      </small>
                    </div>
                  )}
                  {linkedinOffered && (
                  <div className="users-form-field">
                    <label>{t('clients.linkedinAccId')}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        style={{ flex: 1 }}
                        value={editForm.linkedin_ad_account_id}
                        placeholder={t('clients.linkedinPh')}
                        readOnly
                        title={t('clients.linkedinSetTitle')}
                      />
                      <button
                        type="button"
                        className="users-btn users-btn-secondary"
                        onClick={() => connectExternal('linkedin')}
                        title={t('clients.linkedinAuthTitle')}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {editForm.linkedin_ad_account_id ? t('clients.reconnect') : t('clients.connectLinkedin')}
                      </button>
                      {editForm.linkedin_ad_account_id && (
                        <button type="button" className="users-btn users-btn-secondary" onClick={() => disconnectExternal('linkedin')} title={t('clients.disconnectTitle')} style={{ whiteSpace: 'nowrap', color: '#c0392b' }}>{t('clients.disconnect')}</button>
                      )}
                    </div>
                    {editForm.linkedin_ad_account_id && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 13 }}>
                        <span style={{ color: '#777' }}>{t('clients.adsFromOrg')}</span>
                        <span style={{ fontWeight: 600, color: editForm.linkedin_org_id ? '#1b1430' : '#c0392b' }}>
                          {editForm.linkedin_org_name || editForm.linkedin_org_id || t('clients.noneRequired')}
                        </span>
                        <button type="button" className="users-btn users-btn-secondary" style={{ whiteSpace: 'nowrap' }}
                          onClick={() => loadExternalAccounts('linkedin_org')}>{t('clients.selectOrg')}</button>
                      </div>
                    )}
                  </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <button
                      type="button"
                      className="users-btn users-btn-secondary"
                      onClick={handleExternalSync}
                      disabled={syncRunning || (!editForm.google_ads_customer_id.trim() && !editForm.meta_ad_account_id.trim())}
                    >
                      {syncRunning ? t('clients.syncing') : t('clients.syncNow')}
                    </button>
                    {externalSynced && (
                      <span style={{ fontSize: 12, color: 'var(--text-secondary, #777)' }}>
                        {t('clients.lastSynced', { when: new Date(externalSynced).toLocaleString(currentLocale()) })}
                      </span>
                    )}
                  </div>
                  {syncResult && (
                    <pre style={{ marginTop: 10, padding: 10, background: 'var(--bg-tertiary, #f5f5f7)', borderRadius: 8, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                      {syncResult}
                    </pre>
                  )}
                </div>
              )}
            </div>
            <div className="users-modal-footer">
              <button className="users-btn users-btn-secondary" onClick={() => { setShowEditModal(false); setEditClient(null); setEditError(''); }}>
                {t('common.cancel')}
              </button>
              <button
                className="users-btn users-btn-primary"
                onClick={handleSaveEdit}
                disabled={saving || !editForm.display_name.trim()}
              >
                {saving ? t('clients.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && selectedClient && (
        <div className="users-modal-overlay" onClick={() => { setShowCredentialsModal(false); setNewPassword(''); setShowPassword(false); setSelectedClient(null); }}>
          <div className="users-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="users-modal-header">
              <h2>{t('clients.credTitle')}</h2>
              <button className="users-modal-close" onClick={() => { setShowCredentialsModal(false); setNewPassword(''); setShowPassword(false); setSelectedClient(null); }}>✕</button>
            </div>
            <div className="users-modal-body">
              <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                {selectedClient.display_name}
              </p>

              {/* Login */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>{t('clients.loginShort')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <code style={{
                    flex: 1,
                    background: 'var(--bg-tertiary)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'monospace'
                  }}>
                    {selectedClient.client_user?.email}
                  </code>
                  <button
                    onClick={() => copyToClipboard(selectedClient.client_user?.email || '', 'cred-login')}
                    style={{
                      background: copiedField === 'cred-login' ? '#2ecc71' : 'var(--bg-tertiary)',
                      color: copiedField === 'cred-login' ? 'white' : 'inherit',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background 0.15s'
                    }}
                    title={copiedField === 'cred-login' ? t('clients.copied') : t('clients.copy')}
                  >
                    {copiedField === 'cred-login' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 18, height: 18 }}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Password */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>{t('clients.passwordShort')}</label>
                {(() => {
                  // Show new password if just reset, otherwise show stored password
                  const displayPassword = newPassword || selectedClient.client_user?.password;
                  const isNewPassword = !!newPassword;

                  if (!displayPassword) {
                    return (
                      <div style={{
                        background: 'var(--bg-tertiary)',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '14px'
                      }}>
                        {t('clients.noPasswordStored')}
                      </div>
                    );
                  }

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code style={{
                        flex: 1,
                        background: isNewPassword ? 'rgba(46, 204, 113, 0.1)' : 'var(--bg-tertiary)',
                        border: isNewPassword ? '1px solid rgba(46, 204, 113, 0.3)' : 'none',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontFamily: 'monospace',
                        letterSpacing: showPassword ? 'normal' : '2px'
                      }}>
                        {showPassword ? displayPassword : '••••••••'}
                      </code>
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          background: 'var(--bg-tertiary)',
                          border: 'none',
                          padding: '12px',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                        title={t(showPassword ? 'clients.hidePassword' : 'clients.showPassword')}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => copyToClipboard(displayPassword, 'cred-password')}
                        style={{
                          background: (isNewPassword || copiedField === 'cred-password') ? '#2ecc71' : 'var(--bg-tertiary)',
                          color: (isNewPassword || copiedField === 'cred-password') ? 'white' : 'inherit',
                          border: 'none',
                          padding: '12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background 0.15s'
                        }}
                        title={copiedField === 'cred-password' ? t('clients.copied') : t('clients.copy')}
                      >
                        {copiedField === 'cred-password' ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 18, height: 18 }}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })()}
              </div>

              {newPassword && (
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {t('clients.newPasswordGenerated')}
                </p>
              )}
            </div>
            <div className="users-modal-footer">
              <button
                className="users-btn users-btn-secondary"
                onClick={handleResetPassword}
                disabled={resettingPassword}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16, marginRight: 6 }}>
                  <path d="M23 4v6h-6"/>
                  <path d="M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                {resettingPassword ? t('clients.resetting') : t('clients.resetPassword')}
              </button>
              <button className="users-btn users-btn-primary" onClick={() => { setShowCredentialsModal(false); setNewPassword(''); setShowPassword(false); setSelectedClient(null); }}>
                {t('clients.done')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* External account picker modal (Google Ads / Meta) — shown right after Connect */}
      {pickerKind && (
        <div
          onClick={() => setPickerKind(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,16,40,0.45)', display: 'flex',
                   alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: '22px 22px 18px', width: 440, maxWidth: '92%',
                     maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>
              {t(pickerKind === 'google' ? 'clients.pickGoogle'
                : pickerKind === 'meta' ? 'clients.pickMeta'
                  : pickerKind === 'tiktok' ? 'clients.pickTiktok'
                    : pickerKind === 'yandex' ? 'clients.pickYandex'
                      : pickerKind === 'meta_page' ? 'clients.pickMetaPage'
                        : pickerKind === 'tiktok_identity' ? 'clients.pickTiktokIdentity'
                          : pickerKind === 'linkedin_org' ? 'clients.pickLinkedinOrg'
                            : 'clients.pickLinkedinAcc')}
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#777' }}>
              {gLoading
                ? t('clients.fetchingAccounts')
                : pickerKind === 'meta'
                  ? t('clients.metaUnlinked', {
                    unlinked: gAccounts.filter((a) => !a.linked).length, total: gAccounts.length,
                  })
                  : pickerKind === 'meta_page'
                    ? t('clients.pagesShared', { count: gAccounts.length })
                    : t('clients.accountsAvail', { count: gAccounts.length })}
            </p>
            {gLoading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '28px 0', color: '#777' }}>
                <span className="btn-spinner" style={{ width: 22, height: 22, borderWidth: 3 }}></span>
                {t('clients.loadingAccounts')}
              </div>
            )}
            {!gLoading && pickerError && (
              <div style={{ padding: '16px', color: '#8a5a00', background: '#fff7e6', border: '1px solid #ffe1a8',
                            borderRadius: 8, fontSize: 13, lineHeight: 1.5 }}>
                {pickerError}
              </div>
            )}
            {!gLoading && !pickerError && gAccounts.length === 0 && (
              <div style={{ padding: '20px 0', color: '#999', textAlign: 'center' }}>{t('clients.noAccounts')}</div>
            )}

            {/* Rich picker: search + (Meta) grouped by owner; bold name, unlinked-first, show-more */}
            {!gLoading && !pickerError && (pickerKind === 'meta' || pickerKind === 'meta_page' || pickerKind === 'tiktok') && gAccounts.length > 0 && (() => {
              const isPages = pickerKind === 'meta_page';
              const isMetaAcc = pickerKind === 'meta';
              const hasOwnerGroups = pickerKind === 'meta' || pickerKind === 'meta_page';   // Meta carries owner
              const hasLinked = pickerKind === 'meta' || pickerKind === 'tiktok';           // accounts get linked
              const secondary = (id: string) => isMetaAcc ? `act_${id}` : `ID ${id}`;
              const q = pickerSearch.trim().toLowerCase();
              const match = (a: typeof gAccounts[number]) => !q
                || a.id.includes(q) || (a.name || '').toLowerCase().includes(q) || (a.owner || '').toLowerCase().includes(q);
              const filtered = gAccounts.filter(match);
              // Accounts (Meta/TikTok): hide already-linked until search / Show all.
              const visible = hasLinked ? ((q || pickerShowAll) ? filtered : filtered.filter((a) => !a.linked)) : filtered;
              const hidden = filtered.length - visible.length;
              const groups: { owner: string; items: typeof gAccounts }[] = [];
              const gi: Record<string, number> = {};
              visible.forEach((a) => {
                const o = hasOwnerGroups ? (a.owner || t('clients.unknownOwner')) : '';
                if (gi[o] === undefined) { gi[o] = groups.length; groups.push({ owner: o, items: [] }); }
                groups[gi[o]].items.push(a);
              });
              const curId = isPages ? editForm.meta_page_id
                : isMetaAcc ? editForm.meta_ad_account_id.replace(/\D/g, '')
                  : editForm.tiktok_advertiser_id;
              return (
                <>
                  <input
                    type="text"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder={t(isPages ? 'clients.searchPagesPh' : 'clients.searchAccountsPh')}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e5ea',
                             fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
                  />
                  <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {groups.length === 0 && (
                      <div style={{ padding: '16px 0', color: '#999', textAlign: 'center' }}>{t('clients.noMatches')}</div>
                    )}
                    {groups.map((group) => (
                      <div key={group.owner || '_'} style={{ marginBottom: 6 }}>
                        {hasOwnerGroups && (
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                                        color: '#8E3A9A', padding: '6px 2px 4px' }}>
                            {group.owner}
                          </div>
                        )}
                        {group.items.map((a) => {
                          const selected = curId === a.id;
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => chooseAccount(a)}
                              style={{ width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                                       border: `1px solid ${selected ? '#8E3A9A' : '#e5e5ea'}`, marginBottom: 4,
                                       background: selected ? '#f6edf8' : (a.linked ? '#fafafa' : '#fff'),
                                       display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: '#1b1430', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {a.name || secondary(a.id)}
                                </div>
                                <div style={{ color: '#aaa', fontSize: 11 }}>{secondary(a.id)}</div>
                              </div>
                              {hasLinked && a.linked && (
                                <span style={{ fontSize: 11, color: '#999', background: '#eee', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                                  {a.linked_to ? t('clients.linkedToBadge', { name: a.linked_to }) : t('clients.linkedBadge')}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                    {hasLinked && !q && hidden > 0 && (
                      <button
                        type="button"
                        onClick={() => setPickerShowAll(true)}
                        style={{ background: 'none', border: 'none', color: '#8E3A9A', fontSize: 13, fontWeight: 600,
                                 cursor: 'pointer', padding: '8px 2px', textAlign: 'left' }}
                      >
                        {t('clients.showLinked', { count: hidden })}
                      </button>
                    )}
                  </div>
                </>
              );
            })()}

            {/* Other platforms + sender-identity pickers: simple flat list */}
            {!gLoading && !pickerError && pickerKind !== 'meta' && pickerKind !== 'meta_page' && pickerKind !== 'tiktok' && gAccounts.length > 0 && (
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {gAccounts.map((a) => {
                  const current = pickerKind === 'google' ? editForm.google_ads_customer_id
                    : pickerKind === 'yandex' ? editForm.yandex_client_login
                      : pickerKind === 'tiktok_identity' ? editForm.tiktok_identity_id
                        : pickerKind === 'linkedin_org' ? editForm.linkedin_org_id
                          : editForm.linkedin_ad_account_id;
                  const alnum = pickerKind === 'yandex' || pickerKind === 'tiktok_identity' || pickerKind === 'linkedin_org';
                  const selected = alnum ? current === a.id : current.replace(/\D/g, '') === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => chooseAccount(a)}
                      style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                               border: `1px solid ${selected ? '#8E3A9A' : '#e5e5ea'}`,
                               background: selected ? '#f6edf8' : '#fff', fontSize: 14 }}
                    >
                      {pickerKind === 'tiktok_identity' || pickerKind === 'linkedin_org' ? (
                        <>
                          <span style={{ fontWeight: 600 }}>{a.name || a.id}</span>
                          <span style={{ color: '#bbb', fontSize: 11, marginLeft: 8 }}>ID {a.id.slice(0, 10)}…</span>
                        </>
                      ) : (
                        <>
                          <span style={{ fontWeight: 600 }}>{fmtAcc(pickerKind, a.id)}</span>
                          {a.name ? <span style={{ color: '#777' }}> — {a.name}</span> : null}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button type="button" className="users-btn users-btn-secondary" onClick={() => setPickerKind(null)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientsPage;
